import { api } from './api';

/**
 * Clients for the device-linking flow.
 *
 * The BE (services/api/internal/linking/service.go) treats the ticket as
 * opaque ciphertext: the existing device builds + HPKE-seals a `LinkingTicket`
 * to the new device's ephemeral pubkey, uploads the sealed bytes, and tells
 * the new device the resulting `ticket_id`. The new device GETs the sealed
 * blob and decrypts it with its matching private key. The BE never sees
 * plaintext, never learns the new device's identity, and enforces a
 * single-use + 5-minute TTL on the ticket.
 *
 * The cryptographic primitives (`buildLinkingTicket`, `sealLinkingTicket`,
 * `openLinkingTicket`, `consumeLinkingTicket`) live in the MLS SDK — see
 * `packages/ui/src/chat/linkingClient.ts` (added alongside this file) for
 * the end-to-end wrapper that combines the network call with the SDK
 * primitives.
 */
export interface PostTicketRequest {
  /** Base64url-encoded HPKE ciphertext. */
  sealed: string;

  /**
   * SHA-256(eph_pk) of the new device's ephemeral X25519 pubkey,
   * base64url-encoded. When set, the API forwards the sealed bytes
   * to ws-fanout's pre-auth `/v1/linking/stream` channel keyed by
   * this hash so the new device pairs in one QR scan. Optional —
   * omitting it falls back to the legacy two-QR (existing device
   * shows ticket id, new device scans it) flow.
   */
  delivery_pubkey_hash?: string;

  /**
   * Second HPKE-sealed envelope produced by the existing device
   * alongside the SDK LinkingTicket — sealed against the same new-
   * device eph pubkey but with the info string `ping/link-auth/v1`.
   * Carries the auth-layer `user_identity_pubkey` and a BE-format
   * `device_binding_sig` over the new device's id. The new device
   * opens this and feeds the cleartext to `POST /v1/auth/link-verify`
   * to mint tokens without an OTP roundtrip. Optional — when
   * omitted the new device falls back to the legacy "re-OTP after
   * linking" path.
   */
  auth_sealed?: string;
}

export interface PostTicketResponse {
  ticket_id: string;
  /** Unix seconds. */
  expires_at: number;
  /**
   * Pre-auth WS delivery count from ws-fanout's
   * `/internal/push/linking`. See `services/api/internal/linking/
   * types.go` for the encoding contract:
   *   - undefined: request lacked `delivery_pubkey_hash`
   *   - 0:         pushed, no subscriber listening yet
   *   - >0:        pushed and delivered to N sockets
   *   - -1:        pusher disabled server-side
   *                (WS_FANOUT_INTERNAL_URL unset) — UI should
   *                fall back to the 2-QR path
   */
  push_delivered?: number;
}

export interface GetTicketResponse {
  sealed: string;
  /** Mirror of `PostTicketRequest.auth_sealed` — empty for legacy
   * tickets posted before the link-verify flow shipped. */
  auth_sealed?: string;
}

// Building / fetching a pairing ticket is a FOREGROUND user action. A 401
// here must NOT tear down the existing (E) device's session — it should
// surface as an inline "couldn't build pairing code, try again". The
// `X-Ping-Skip-Reauth` header opts both the global refresh-interceptor and
// the `api` 401 handler out of the reactive refresh→logout, so a failed
// linking call throws to the caller instead of logging the user out.
// (The proactive pre-expiry refresh still runs for these requests, so a
// valid session keeps a fresh access token regardless.)
const SKIP_REAUTH_HEADERS = { 'X-Ping-Skip-Reauth': '1' } as const;

export async function postLinkingTicket(req: PostTicketRequest): Promise<PostTicketResponse> {
  return api.post<PostTicketResponse>('/v1/linking/tickets', req, {
    headers: { ...SKIP_REAUTH_HEADERS },
  });
}

export async function getLinkingTicket(ticketId: string): Promise<GetTicketResponse> {
  return api.get<GetTicketResponse>(`/v1/linking/tickets/${encodeURIComponent(ticketId)}`, {
    headers: { ...SKIP_REAUTH_HEADERS },
  });
}
